import { API } from "@js/api";
import type { SaveFilterPayload } from "@js/apps/common/components/save-filters/types";
import type { RootState } from "@js/store";
import type {
  FreelancerExternalProfile,
  PopularLocation,
} from "@js/types/common";
import type {
  Freelancer,
  FreelancerCertificate,
  FreelancerForNodeStaff,
  FreelancerPublic,
  FreelancerRole,
  FreelancerSkill,
  FreelancerWorkExperience,
  JobInvitations,
  ProfileCompletion,
  PutFreelancerWorkExperiencePayload,
  SavedTalentFilter,
  WorkSampleItem,
} from "@js/types/freelancer";
import type { Talent } from "@js/types/freelancer";
import type { PaginatedResult } from "@js/types/generic";
import type {
  FreelancerBid,
  FreelancerBidListItem,
  FreelancerBidParams,
  FreelancerOffer,
  Job,
  PublicFreelancerBidParams,
} from "@js/types/jobs";
import type { UploadFile } from "@js/types/uploads";
import { getUseQueryHookWithDefaultOptions } from "@js/utils/store";

import type { TalentFiltersFetchParams } from "../common/components/filters";

import { fetchFreelancerProfile } from "./actions";
import type {
  CreateWorkExperienceParams,
  UpdateFreelancerProfileParams,
} from "./types";

type FreelancerBidsParams = {
  current?: boolean;
  historical?: boolean;
  accepted?: boolean;
  job?: number;
  ordering?: "-created";
  not_rejected?: boolean;
};

type FreelancerBidsPaginatedParams = FreelancerBidsParams & {
  page_size?: number;
};

type FreelancerJobsParams = {
  completed?: boolean;
  show_jobs_of_removed_users?: boolean;
};

type SaveFreelancerRolePayload = {
  role: number;
  primary: boolean;
  years_experience: number;
};

type UpdateFreelancerRolePayload = {
  id: number;
  role: number;
  primary: boolean;
  years_experience: number;
  primary_freelancer_role?: number;
};

export type TaxFormType = "w8" | "w9";

type GetTaxFormResponse = {
  form_url: string | null;
  form_uploaded_at: string | null;
};

type GetTaxFormsParams = {
  freelancerId: number;
  formType: TaxFormType;
};

type DeleteTaxFormsParams = {
  freelancerId: number;
  formType: TaxFormType;
};

type UpdateTaxFormsResponse = {
  form_url: string | null;
  form_uploaded_at: string | null;
};

type UpdateTaxFormsParams = {
  freelancerId: number;
  formType: TaxFormType;
  formFileId: number;
};

type AddPortfolioItemParams = Omit<WorkSampleItem, "id">;
type AddPortfolioItemResponse = WorkSampleItem;

export type UpdateFreelancerCustomizationPayload = {
  user?: {
    first_name?: string;
    last_name?: string | null;
    cover_photo_id?: number;
    avatar_id?: number;
    avatar?: string;
    introduction_headline?: string | null;
    introduction?: string | null;
    phone_number?: {
      phone_number: string;
      country_iso2: string;
    };
    employment_contact_consent?: boolean;
  };
  external_profiles?: Array<{ new_site: number; public_url: string }>;
  freelancer?: Partial<UpdateFreelancerProfileParams>;
};

type GetFreelancerSkillsResult = Array<Omit<FreelancerSkill, "role">>;

type EditFreelancerSkillsParams = {
  id: number;
  new_skills: Array<{
    new_skill: number;
    is_superpower: boolean;
  }>;
};

type ResumeExtractedEducation = {
  degree: string | null;
  area_of_study: string | null;
  institution: string | null;
  year: number | null;
};

type ResumeExtractedJobTitle = {
  title: string;
  years: string | null;
};

type ResumeExtractedSkillsCompetencies = {
  primary_skills: string[];
  secondary_skills: string[];
  recently_active_skills: string[];
};

export type ResumeExtractedDataLocation = {
  region?: string;
  country?: string;
  state?: string;
};

export type ResumeExtractedDataContactInfo = {
  email: string | null;
  github: string | null;
  linkedin: string | null;
  phone: string | null;
  website: string | null;
};

export type ResumeExtractedData = {
  id: number;
  created: string;
  modified: string;
  educations: ResumeExtractedEducation[] | null;
  years_of_experience: number | null;
  job_titles: ResumeExtractedJobTitle[] | null;
  skills_competencies: ResumeExtractedSkillsCompetencies | null;
  companies: string[] | null;
  contact_info: ResumeExtractedDataContactInfo | null;
  location_info: ResumeExtractedDataLocation | null;
};

type GetFreelancerResumeExtractedDataResponse = {
  data: ResumeExtractedData | null;
  status: EnumType<typeof ENUMS.ADASyncStatus> | null;
};

export type GetFreelancerResumeExtractedBioAndHeadlineResponse = {
  headline: string | null;
  bio: string | null;
};

export type TalentSearchMinimalResult = {
  id: number;
  name: string;
};

export type TalentSearchParams = {
  search?: string;
  public_name_search?: boolean;
};

type CertificateSearchResult = {
  id: number;
  name: string;
  is_default: boolean;
  order: number | null;
};

type CertificateSearchParams = {
  search?: string;
  limit?: number;
};

type CompaniesSearchResult = PaginatedResult<{
  id: number;
  name: string;
  is_default: boolean;
  order: number | null;
}>;

type CompaniesSearchParams = {
  search?: string;
  page: number;
  page_size: number;
  limit?: number;
};

type DegreesSearchResult = {
  id: number;
  name: string;
  is_default: boolean;
  order: number | null;
};

type DegreesSearchParams = {
  search?: string;
  limit?: number;
};

type SchoolsSearchResult = {
  id: number;
  name: string;
  is_default: boolean;
  order: number | null;
};

type SchoolsSearchParams = {
  search?: string;
  limit?: number;
};

type GetFreelancerJobOffersParams = {
  section: EnumType<typeof ENUMS.TalentOffersSectionFilter>;
  order_by?: "id";
};

type FreelancerLocationResponse = {
  is_location_supported: boolean;
};

export type CreatePublicFreelancerBidResponse = FreelancerBid & {
  user_already_registered?: boolean;
};

export const freelancerApi = API.injectEndpoints({
  endpoints: (build) => ({
    getFreelancerPublicProfile: build.query<
      FreelancerPublic | FreelancerForNodeStaff,
      number
    >({
      query: (id) => ({
        url: `/freelancers/${id}`,
        method: "GET",
      }),
      providesTags: ["FreelancerPublicProfile"],
    }),

    getTalents: build.query<
      PaginatedResult<Talent>,
      Partial<TalentFiltersFetchParams> | void
    >({
      query: (filters) => {
        const role = filters?.role;
        const skills = filters?.skills;

        return {
          url: `/talent/`,
          method: "GET",
          params: {
            ...filters,
            role: role?.join(","),
            skills: skills?.join(","),
          },
        };
      },
    }),
    getTalentSearch: build.query<
      TalentSearchMinimalResult[],
      TalentSearchParams
    >({
      query: (params?) => {
        return {
          url: `/talent/`,
          method: "GET",
          params: {
            limit: 100,
            public_name_search: true,
            ...params,
          },
        };
      },
      transformResponse: (
        result: PaginatedResult<TalentSearchMinimalResult>,
      ) => {
        return result.results;
      },
    }),
    getTalent: build.query<
      TalentSearchMinimalResult,
      { id: number; public_name_search?: boolean }
    >({
      query: ({ id, ...params }) => ({
        url: `/talent/${id}/`,
        method: "GET",
        params,
      }),
    }),

    getSavedTalentFilters: build.query<SavedTalentFilter[], void>({
      query: () => ({
        url: `/talent_filters/`,
        method: "GET",
      }),
      providesTags: ["SavedTalentFilters"],
    }),

    saveTalentFilter: build.mutation<SavedTalentFilter, SaveFilterPayload>({
      query: (values) => {
        const filters = values.filters;
        const role = filters.role && filters.role.join(",");
        const skills = filters.skills && filters.skills.join(",");

        return {
          url: `/talent_filters/`,
          method: "POST",
          data: {
            ...values,
            filters: { ...filters, role: role, skills: skills },
          },
        };
      },
      invalidatesTags: ["SavedTalentFilters"],
    }),

    deleteTalentFilter: build.mutation<
      void,
      Pick<SavedTalentFilter, "id" | "name">
    >({
      query: ({ id }) => ({
        url: `/talent_filters/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["SavedTalentFilters"],
    }),

    updatePortfolioItemsPositions: build.mutation<
      void,
      { itemPositions: WorkSampleItem[] }
    >({
      query: ({ itemPositions }) => ({
        url: "/manage_portfolio_items/update_positions/",
        method: "POST",
        data: {
          item_positions: itemPositions,
        },
      }),
      invalidatesTags: ["FreelancerPublicProfile"],
    }),
    updateCertificatesPositions: build.mutation<
      void,
      { itemPositions: FreelancerCertificate[] }
    >({
      query: ({ itemPositions }) => ({
        url: "/freelancer_certificates/update_positions/",
        method: "POST",
        data: {
          item_positions: itemPositions,
        },
      }),
      invalidatesTags: ["FreelancerPublicProfile"],
    }),
    getFreelancerRoles: build.query<FreelancerRole[], void>({
      query: () => ({
        url: "/freelancer_roles/",
        method: "GET",
      }),
      providesTags: ["FreelancerRoles"],
    }),

    saveFreelancerRole: build.mutation<
      Pick<FreelancerRole, "id">,
      SaveFreelancerRolePayload
    >({
      query: (role) => ({
        url: "/freelancer_roles/",
        method: "POST",
        data: role,
      }),
      invalidatesTags: ["FreelancerRoles", "FreelancerProfileCompletion"],
    }),

    updateFreelancerRole: build.mutation<
      Pick<FreelancerRole, "id">,
      UpdateFreelancerRolePayload
    >({
      query: ({ id, ...data }) => ({
        url: `/freelancer_roles/${id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: ["FreelancerRoles"],
    }),

    removeFreelancerRole: build.mutation<void, FreelancerRole["id"]>({
      query: (id) => ({
        url: `/freelancer_roles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FreelancerRoles"],
    }),
    getFreelancerBids: build.query<
      FreelancerBidListItem[],
      FreelancerBidsParams | void
    >({
      query: (params) => ({
        url: "/freelancer_bids/",
        method: "GET",
        params: {
          ...params,
          page_size: 0, // backend returns a non-paginated response when page_size 0 is passed
        },
      }),
      providesTags: ["FreelancerBids"],
    }),
    getFreelancerBidsPaginated: build.query<
      PaginatedResult<FreelancerBidListItem>,
      FreelancerBidsPaginatedParams | void
    >({
      query: (params) => ({
        url: "/freelancer_bids/",
        method: "GET",
        params: {
          ...params,
          page_size: params?.page_size === 0 ? 1 : params?.page_size,
        },
      }),
      providesTags: ["FreelancerBids"],
    }),
    getFreelancerBid: build.query<FreelancerBid, { id?: string }>({
      query: ({ id }) => ({
        url: `/freelancer_bids/${id}/`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    reportFreelancerBid: build.mutation<
      FreelancerBid,
      {
        id: number;
        reason: EnumType<typeof ENUMS.BidReportReasons>;
        message_to_moderator?: string;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/freelancer_bids/${id}/report`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["FreelancerBids"],
    }),
    createFreelancerBid: build.mutation<FreelancerBid, FreelancerBidParams>({
      query: (data) => ({
        url: "/freelancer_bids/",
        method: "POST",
        data,
      }),
      invalidatesTags: [
        { type: "Jobs", id: "LIST" },
        "FreelancerBids",
        "SavedJobs",
        "RelevantJobs",
      ],
    }),
    createPublicFreelancerBid: build.mutation<
      CreatePublicFreelancerBidResponse,
      PublicFreelancerBidParams
    >({
      query: ({ query_params, ...data }) => ({
        url: "/public_create_bid/",
        method: "POST",
        data,
        params: query_params,
      }),
      invalidatesTags: [
        { type: "Jobs", id: "LIST" },
        "FreelancerBids",
        "SavedJobs",
        "RelevantJobs",
      ],
    }),
    updateFreelancerBid: build.mutation<
      FreelancerBid,
      { id?: number; values: FreelancerBidParams }
    >({
      query: ({ id, values }) => ({
        url: `/freelancer_bids/${id}/`,
        method: "PUT",
        data: values,
      }),
      invalidatesTags: ["FreelancerBids"],
    }),
    cancelFreelancerBid: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/freelancer_bids/${id}/cancel_bid/`,
        method: "POST",
      }),
      invalidatesTags: ["FreelancerBids"],
    }),
    getFreelancerJobs: build.query<Job[], FreelancerJobsParams | void>({
      query: (params) => ({
        url: "/freelancer_jobs/",
        method: "GET",
        params,
      }),
    }),
    getFreelancerJobOffers: build.query<
      FreelancerOffer[],
      GetFreelancerJobOffersParams
    >({
      query: ({ section, order_by }) => ({
        url: "/freelancer_offers/",
        method: "GET",
        params: { section, order_by },
      }),
      providesTags: ["FreelancerJobOffers"],
    }),
    deleteResume: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/manage_freelancers/${id}/delete_resume`,
        method: "DELETE",
      }),
      invalidatesTags: ["FreelancerPublicProfile"],
    }),
    uploadResume: build.mutation<
      Freelancer,
      { id: number; resume: UploadFile["id"] }
    >({
      query: ({ id, resume }) => ({
        url: `/manage_freelancers/${id}/`,
        method: "PATCH",
        data: {
          resume,
        },
      }),
      invalidatesTags: ["FreelancerPublicProfile"],
    }),
    updateFreelancerCustomization: build.mutation<
      Freelancer,
      { id: number; data: UpdateFreelancerCustomizationPayload }
    >({
      query: ({ id, data }) => ({
        url: `/manage_talent_customization/${id}/`,
        method: "PUT",
        data,
      }),
      invalidatesTags: [
        "FreelancerExternalProfiles",
        "FreelancerPublicProfile",
        "FreelancerLocation",
      ],
    }),
    getTaxForm: build.query<GetTaxFormResponse, GetTaxFormsParams>({
      query: ({ freelancerId, formType }) => ({
        url: `/manage_freelancers/${freelancerId}/${formType}_form`,
        method: "GET",
      }),
      providesTags: (_result, _error, arg) => [
        { type: "TaxForm", id: arg.formType },
      ],
    }),

    deleteTaxForm: build.mutation<void, DeleteTaxFormsParams>({
      query: ({ freelancerId, formType }) => ({
        url: `/manage_freelancers/${freelancerId}/${formType}_form`,
        method: "DELETE",
      }),

      async onQueryStarted(
        { freelancerId, formType },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          freelancerApi.util.updateQueryData(
            "getTaxForm",
            { freelancerId, formType },
            () => {
              return { form_url: null, form_uploaded_at: null };
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (_error) {
          patch.undo();

          dispatch(
            freelancerApi.util.invalidateTags([
              { type: "TaxForm", id: formType },
            ]),
          );
        }
      },
    }),

    updateTaxForm: build.mutation<UpdateTaxFormsResponse, UpdateTaxFormsParams>(
      {
        query: ({ freelancerId, formType, formFileId }) => ({
          url: `/manage_freelancers/${freelancerId}/${formType}_form`,
          method: "PATCH",
          data: { form: formFileId },
        }),

        async onQueryStarted(
          { freelancerId, formType },
          { dispatch, queryFulfilled },
        ) {
          try {
            const { data: updatedFormData } = await queryFulfilled;
            dispatch(
              freelancerApi.util.updateQueryData(
                "getTaxForm",
                { freelancerId, formType },
                () => {
                  return updatedFormData;
                },
              ),
            );
          } catch (_error) {
            dispatch(
              freelancerApi.util.invalidateTags([
                { type: "TaxForm", id: formType },
              ]),
            );
          }
        },
      },
    ),
    getFreelancerJobInvitations: build.query<JobInvitations, void>({
      query: () => ({
        url: "/jobs/freelancer_invitations/",
        method: "GET",
      }),
      providesTags: ["FreelancerJobInvitations"],
    }),
    getFreelancerWorkExperience: build.query<FreelancerWorkExperience[], void>({
      query: () => ({
        url: "/freelancer_work_experience/",
        method: "GET",
      }),
      providesTags: ["FreelancerWorkExperience"],
      keepUnusedDataFor: 100000000,
    }),
    putFreelancerWorkExperience: build.mutation<
      FreelancerWorkExperience[],
      PutFreelancerWorkExperiencePayload
    >({
      query: (data) => ({
        url: "/freelancer_work_experience/batch",
        method: "PUT",
        data,
      }),
      invalidatesTags: ["FreelancerWorkExperience"],
    }),
    getFreelancerSkills: build.query<GetFreelancerSkillsResult, { id: number }>(
      {
        query: ({ id }) => ({
          url: `/manage_freelancers/${id}/skills`,
          method: "GET",
        }),
        providesTags: ["FreelancerSkills"],
        keepUnusedDataFor: 100000000,
        transformResponse: (response: {
          freelancer_skills: GetFreelancerSkillsResult;
        }): GetFreelancerSkillsResult => {
          return response.freelancer_skills ?? [];
        },
      },
    ),
    editFreelancerSkills: build.mutation<
      { freelancer_skills: GetFreelancerSkillsResult },
      EditFreelancerSkillsParams
    >({
      query: ({ id, ...data }) => ({
        url: `/manage_freelancers/${id}/skills`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: [
        "FreelancerSkills",
        "FreelancerProfileCompletion",
        "FreelancerPublicProfile",
      ],
    }),
    getFreelancerResumeExtractedData: build.query<
      GetFreelancerResumeExtractedDataResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/manage_freelancers/${id}/resume_extracted_data`,
        method: "GET",
      }),
      providesTags: ["ResumeExtractedData"],
    }),
    getFreelancerResumeExtractedBioAndHeadline: build.query<
      GetFreelancerResumeExtractedBioAndHeadlineResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/manage_freelancers/${id}/generate_bio_and_headline`,
        method: "GET",
      }),
      keepUnusedDataFor: 100000,
      providesTags: ["ResumeExtractedData"],
    }),
    addPortfolioItem: build.mutation<
      AddPortfolioItemResponse,
      AddPortfolioItemParams
    >({
      query: (data) => ({
        url: `/manage_portfolio_items/`,
        method: "POST",
        data,
      }),
      invalidatesTags: [
        "FreelancerProfileCompletion",
        "FreelancerPublicProfile",
      ],
    }),
    deletePortfolioItem: build.mutation<void, number>({
      query: (id) => ({
        url: `/manage_portfolio_items/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["FreelancerPublicProfile"],
    }),
    updatePortfolioItem: build.mutation<WorkSampleItem, WorkSampleItem>({
      query: (data) => ({
        url: `/manage_portfolio_items/${data.id}/`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["FreelancerPublicProfile"],
    }),
    getTalentPopularLocations: build.query<Array<PopularLocation>, void>({
      query: () => ({
        url: "/frequent_talent_location_filters/",
        method: "GET",
      }),
    }),
    getFreelancerProfileCompletion: build.query<
      ProfileCompletion,
      { freelancerId: number }
    >({
      query: ({ freelancerId }) => ({
        url: `/freelancers/${freelancerId}/get_profile_completion_percent/`,
        method: "GET",
      }),
      keepUnusedDataFor: 1000000000,
      providesTags: ["FreelancerProfileCompletion"],
    }),

    getFreelancerExternalProfiles: build.query<
      FreelancerExternalProfile[],
      void
    >({
      query: () => ({
        url: `/freelancer_external_profiles/`,
        method: "GET",
      }),
      keepUnusedDataFor: 1000000000,
      providesTags: ["FreelancerExternalProfiles"],
    }),

    createWorkExperience: build.mutation<
      FreelancerWorkExperience,
      CreateWorkExperienceParams
    >({
      query: (values) => ({
        url: "/freelancer_work_experience/",
        method: "POST",
        data: values,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const state = getState() as RootState;
          const freelancerId = state.auth.user?.freelancer as number;
          void dispatch(fetchFreelancerProfile(freelancerId));
        } catch (_err) {
          // do nothing
        }
      },
      invalidatesTags: [
        "FreelancerWorkExperience",
        "FreelancerPublicProfile",
        "FreelancerProfileCompletion",
      ],
    }),
    deleteWorkExperience: build.mutation<undefined, number>({
      query: (id) => ({
        url: `/freelancer_work_experience/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["FreelancerWorkExperience", "FreelancerPublicProfile"],
    }),
    updateWorkExperience: build.mutation<
      FreelancerWorkExperience,
      CreateWorkExperienceParams
    >({
      query: ({ id, ...values }) => ({
        url: `/freelancer_work_experience/${id}/`,
        method: "PATCH",
        data: values,
      }),
      invalidatesTags: ["FreelancerWorkExperience", "FreelancerPublicProfile"],
    }),
    getCertificates: build.query<
      CertificateSearchResult[],
      CertificateSearchParams
    >({
      query: ({ limit = 100, ...params }) => ({
        url: `/certificates/`,
        method: "GET",
        params: {
          limit,
          ...params,
        },
      }),
    }),

    getDegrees: build.query<DegreesSearchResult[], DegreesSearchParams>({
      query: ({ limit = 100, ...params }) => ({
        url: `/degrees/`,
        method: "GET",
        params: {
          limit,
          ...params,
        },
      }),
    }),
    getSchools: build.query<SchoolsSearchResult[], SchoolsSearchParams>({
      query: ({ limit = 100, ...params }) => ({
        url: `/schools/`,
        method: "GET",
        params: {
          limit,
          ...params,
        },
      }),
    }),
    getFreelancerLocation: build.query<
      FreelancerLocationResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/freelancers/${id}/location`,
        method: "GET",
      }),
      providesTags: ["FreelancerLocation"],
    }),
    sendSmsToFreelancer: build.mutation<
      void,
      { freelancerId: number; message: string }
    >({
      query: ({ freelancerId, message }) => ({
        url: `/freelancers/${freelancerId}/send_sms/`,
        method: "POST",
        data: { message },
      }),
    }),
    bulkSendSms: build.mutation<
      Array<{
        freelancer_id: number;
        success: boolean;
        error: string | null;
      }>,
      { message: string; freelancer_ids: number[]; job_url?: string }
    >({
      query: ({ message, freelancer_ids, job_url }) => ({
        url: "/expert_contributors/bulk_send_sms/",
        method: "POST",
        data: { message, freelancer_ids, job_url },
      }),
    }),
  }),
});

type CreateCompanyTaxonomyPayload = { name: string };

type CompanyTaxonomyResponse = CreateCompanyTaxonomyPayload & { id: number };

type BulkCreateCompaniesPayload = CreateCompanyTaxonomyPayload[];

type BulkCreateCompaniesResponse = { companies: CompanyTaxonomyResponse[] };

const freelancerTaxonomiesApi = API.injectEndpoints({
  endpoints: (build) => ({
    createCompaniesBulk: build.mutation<
      BulkCreateCompaniesResponse,
      BulkCreateCompaniesPayload
    >({
      query: (payload) => ({
        url: `/companies/bulk_save/`,
        method: "POST",
        data: { companies: payload },
      }),
    }),
    getCompanies: build.query<CompaniesSearchResult, CompaniesSearchParams>({
      query: ({ limit = 100, ...params }) => ({
        url: `/companies/`,
        method: "GET",
        params: {
          limit,
          ...params,
        },
      }),
    }),
  }),
});

export const {
  useGetTalentsQuery,
  useGetSavedTalentFiltersQuery,
  useSaveTalentFilterMutation,
  useDeleteTalentFilterMutation,
  useUpdatePortfolioItemsPositionsMutation,
  useUpdateCertificatesPositionsMutation,
  useGetFreelancerRolesQuery,
  useSaveFreelancerRoleMutation,
  useUpdateFreelancerRoleMutation,
  useRemoveFreelancerRoleMutation,
  useGetFreelancerBidQuery,
  useGetFreelancerBidsQuery,
  useCreateFreelancerBidMutation,
  useCreatePublicFreelancerBidMutation,
  useUpdateFreelancerBidMutation,
  useCancelFreelancerBidMutation,
  useUpdateFreelancerCustomizationMutation,
  useGetFreelancerJobsQuery,
  useDeleteResumeMutation,
  useUploadResumeMutation,
  useGetTaxFormQuery,
  useDeleteTaxFormMutation,
  useUpdateTaxFormMutation,
  useGetFreelancerJobInvitationsQuery,
  useGetFreelancerWorkExperienceQuery,
  useGetFreelancerSkillsQuery,
  useEditFreelancerSkillsMutation,
  useGetFreelancerResumeExtractedDataQuery,
  useAddPortfolioItemMutation,
  useDeletePortfolioItemMutation,
  useUpdatePortfolioItemMutation,
  useGetTalentPopularLocationsQuery,
  useGetFreelancerBidsPaginatedQuery,
  useLazyGetFreelancerBidsPaginatedQuery,
  useGetFreelancerJobOffersQuery,
  useGetFreelancerPublicProfileQuery,
  useCreateWorkExperienceMutation,
  useDeleteWorkExperienceMutation,
  useUpdateWorkExperienceMutation,
  useGetTalentQuery,
  useGetTalentSearchQuery,
  useGetCertificatesQuery,
  useGetDegreesQuery,
  useGetSchoolsQuery,
  useLazyGetFreelancerProfileCompletionQuery,
  useReportFreelancerBidMutation,
  usePutFreelancerWorkExperienceMutation,
  useGetFreelancerLocationQuery,
  useSendSmsToFreelancerMutation,
  useBulkSendSmsMutation,
} = freelancerApi;

export const { useGetCompaniesQuery, useCreateCompaniesBulkMutation } =
  freelancerTaxonomiesApi;

export const useGetFreelancerExternalProfilesQuery =
  getUseQueryHookWithDefaultOptions(
    freelancerApi.useGetFreelancerExternalProfilesQuery,
    { refetchOnMountOrArgChange: false },
  );

export const useGetFreelancerProfileCompletionQuery =
  getUseQueryHookWithDefaultOptions(
    freelancerApi.useGetFreelancerProfileCompletionQuery,
    { refetchOnMountOrArgChange: false },
  );
export const useGetFreelancerResumeExtractedBioAndHeadlineQuery =
  getUseQueryHookWithDefaultOptions(
    freelancerApi.useGetFreelancerResumeExtractedBioAndHeadlineQuery,
    { refetchOnMountOrArgChange: false },
  );
